import { InlineIcon } from "@iconify/react/dist/iconify.js";
import { useMutation } from "@tanstack/react-query";
import _ from "lodash";
import { Fragment, useCallback, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { submitQuotationRequest } from "../../../api/quotation";
import { GlobalModalsContext } from "../../../context";
import { useApoAuth } from "../../../hooks";
import { StyleTypeContext } from "../../../umbilical_tools";
import { CancelButton, CloseButton } from "./componets";
import { InitialSetup } from "./InitialSetup";
import { QuotationRequestChat } from "./QuotationRequestChat";
import { SubmittingQuotationRequest } from "./SubmittingQuotationRequest";
import { SuccessfulQuotationSubmission } from "./SuccessfulQuotationSubmission";
import { UploadQuotationRequestDoc } from "./UploadFileForm";

type UserType = {
  "https://sisitrip.com": {
    mark: {
      complete_user_profile: boolean;
      complete_company_profile: string;
      company_id: number;
      company_type: string;
    };
  };
  nickname: string;
  name: string;
  picture: string;
  updated_at: string;
  email: string;
  email_verified: boolean;
  sub: string;
};

export function NewQuotationRequestModal() {
  const [file, setFile] = useState<File | null>(null);
  const [parsedFile, setParsedFile] = useState<string>("");
  const [quotationRequestReference, setQuotationRequestReference] = useState<string>("");

  const [step, setStep] = useState<
    | "initial"
    | "upload"
    | "parsing_document"
    | "preview_document"
    | "preview"
    | "quotation_request_form"
    | "chat"
    | "submitting_quotation_request"
    | "successful_quotation_submission"
  >("initial");

  const { user, getIdTokenClaims } = useApoAuth();
  const usr = user as UserType;

  const sourceEntity = `${usr["https://sisitrip.com"]?.mark?.company_id}___${usr["https://sisitrip.com"]?.mark?.company_type}`;
  const userName = usr.sub;
  const userEmail = usr.email;

  const { setShowNewQuotationRequestModal } = useContext(GlobalModalsContext) as {
    setShowNewQuotationRequestModal: (show: boolean) => void;
  };
  const { brand } = useContext(StyleTypeContext);

  const onCancel = useCallback(() => {
    setShowNewQuotationRequestModal(false);
  }, [setShowNewQuotationRequestModal]);

  const { source_entity: target_entity } = useSelector(
    (state: { WebsiteMetadata: { metadata: { source_entity: string } } }) => {
      const source_entity = state?.WebsiteMetadata?.metadata?.source_entity;
      return { source_entity };
    },
  );

  const finalizeQRMutation = useMutation({
    mutationFn: async ({
      session_id,
      chat_session_uid,
      extra_comments,
      source_entity,
      target_entity,
      quotation_title,
      due_date,
    }: {
      session_id: string;
      chat_session_uid: string;
      extra_comments: string;
      source_entity: string;
      target_entity: string;
      quotation_title: string;
      due_date: string;
    }) => {
      const token: { __raw: string } = await getIdTokenClaims();

      return submitQuotationRequest({
        session_id,
        token: token?.__raw,
        extra_comments,
        chat_session_uid,
        source_entity,
        target_entity,
        quotation_title,
        due_date,
      });
    },
  });

  // Handle the successful quotation request submission
  useEffect(() => {
    if (!finalizeQRMutation.isSuccess) return;
    setQuotationRequestReference(finalizeQRMutation.data.data.quotation_request_reference);
    setStep("successful_quotation_submission");
  }, [finalizeQRMutation.isSuccess, finalizeQRMutation.data]);

  // Handle the error when submitting the quotation request
  useEffect(() => {
    if (!finalizeQRMutation.isError) return;
    toast.error("Failed to submit quotation request");
  }, [finalizeQRMutation.isError]);

  // Reset the state when the step is changed to initial
  useEffect(() => {
    if (step !== "initial") return;
    setParsedFile("");
    setFile(null);
  }, [step]);

  let modalWidth = "";
  let modalHeight = "";
  if (step === "upload") {
    modalWidth = "w-2/3!";
  } else if (step === "chat") {
    modalWidth = "w-3/4!";
    modalHeight = "h-4/5!";
  } else if (step === "quotation_request_form") {
    modalWidth = "w-4/5!";
  }

  return (
    <div className="Modal QuotationRequestModal">
      <div
        className={`Modal__card QuotationRequestModal__container min-h-1/2 max-h-4/5 ${modalWidth} ${modalHeight} max-w-4/5 overflow-y-hidden grid grid-rows-[auto_1fr_auto]`}
      >
        <div
          className="Modal__card__header QuotationRequestModal__container__header"
          data-brand={brand}
        >
          <h6>{step === "chat" ? `${_.startCase(brand)} Trip Assistant` : "Quotation Request"}</h6>
          <InlineIcon
            icon="ei:close"
            className="QuotationRequestModal__container__header__closeIcon"
            onClick={() => setShowNewQuotationRequestModal(false)}
          />
        </div>
        {step === "initial" && (
          <Fragment>
            <div className="Modal__card__body">
              <InitialSetup
                // onUpload={() => setStep("upload")}
                // onFillForm={() => setStep("quotation_request_form")}
                onChat={() => setStep("chat")}
              />
            </div>
            <div className="Modal__card__actions">
              <CancelButton onCancel={onCancel} />
            </div>
          </Fragment>
        )}
        {step === "upload" && (
          <UploadQuotationRequestDoc
            file={file}
            onFileChange={setFile}
            onBack={() => setStep("initial")}
            onSetParsedFile={(parsedFile) => {
              setParsedFile(parsedFile);
              setStep("quotation_request_form");
            }}
          />
        )}
        {step === "preview_document" && (
          <Fragment>
            <div className="Modal__card__body overflow-hidden">
              <h6>Preview Document</h6>
              <div className="grid border border-gray-200 rounded-md p-3 overflow-auto">
                <p className="whitespace-pre">{parsedFile}</p>
              </div>
            </div>
            <div className="Modal__card__actions">
              <CancelButton onCancel={onCancel} />
            </div>
          </Fragment>
        )}
        {step === "chat" && (
          <QuotationRequestChat
            sourceEntity={sourceEntity}
            targetEntity={target_entity}
            userName={userName}
            userEmail={userEmail}
            onBack={() => setStep("initial")}
            isSubmitting={finalizeQRMutation.isLoading}
            finalizeQuotationRequest={(
              sessionId: string,
              chatSessionUid: string,
              extraComments: string,
              sourceEntity: string,
              targetEntity: string,
              quotationTitle: string,
              dueDate: string,
            ) => {
              finalizeQRMutation.mutate({
                session_id: sessionId,
                chat_session_uid: chatSessionUid,
                extra_comments: extraComments,
                source_entity: sourceEntity,
                target_entity: targetEntity,
                quotation_title: quotationTitle,
                due_date: dueDate,
              });
            }}
          />
        )}
        {step === "submitting_quotation_request" && <SubmittingQuotationRequest />}
        {step === "successful_quotation_submission" && (
          <Fragment>
            <SuccessfulQuotationSubmission reference={quotationRequestReference} />
            <div className="Modal__card__actions">
              <CloseButton onClose={onCancel} />
            </div>
          </Fragment>
        )}
      </div>
    </div>
  );
}
